import { useCallback, useEffect, useState } from "react";
import { formatUnits, parseUnits } from "viem";
import {
  useAccount,
  useChainId,
  usePublicClient,
  useSwitchChain,
  useWalletClient
} from "wagmi";
import { arbitrum } from "wagmi/chains";

import Modal from "components/Modal/Modal";
import TokenSelector from "components/TokenSelector/TokenSelector";
import { withdrawETH, withdrawToken } from "lib/api/contractExecutor";
import { getHlBalances, withdrawFromHl } from "lib/api/hyperliquid";
import { helperToast } from "lib/helperToast";
import { useAuth } from "context/AuthContext/AuthContextProvider";

import { TOKENS } from "sdk/configs/tokens";
import "./CustodyWalletModal.scss";

type WithdrawSource = "custody" | "hyperliquid";

type TabType = 'deposit' | 'withdraw';

const ARBITRUM_CHAIN_ID = 42161;
const GAS_RESERVE_USD = 2.5; // $2.5 gas reserve requirement

const ERC20_ABI = [
  {
    inputs: [{ name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    name: "transfer",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "nonpayable",
    type: "function"
  }
] as const;

type Props = {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  custodyWalletAddress: string;
};

export function CustodyWalletModal({
  isVisible,
  setIsVisible,
  custodyWalletAddress
}: Props) {
  const { address: account, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { token: authToken } = useAuth();

  // Note: useTokensData requires SyntheticsStateContextProvider
  // Since CustodyWallet can be used outside that context (e.g., in header),
  // we'll pass undefined and handle balances differently
  const tokensData = undefined;

  // Get Arbitrum tokens
  const arbitrumTokens = TOKENS[ARBITRUM_CHAIN_ID] || [];

  const [activeTab, setActiveTab] = useState<TabType>('deposit');
  const [withdrawSource, setWithdrawSource] = useState<WithdrawSource>("custody");
  const [selectedToken, setSelectedToken] = useState(arbitrumTokens[0] || null);
  const [amount, setAmount] = useState("");
  const [maxBalance, setMaxBalance] = useState("0.00");
  const [hlUsdcBalance, setHlUsdcBalance] = useState<string | null>(null);
  const [isLoadingHlBalance, setIsLoadingHlBalance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [wrongNetwork, setWrongNetwork] = useState(false);
  const [ethBalance, setEthBalance] = useState("0");
  const [ethPriceUSD, setEthPriceUSD] = useState(0);
  const [hasInsufficientGas, setHasInsufficientGas] = useState(false);
  const [showLowGasWarning, setShowLowGasWarning] = useState(false);

  const isUserRejectedError = useCallback((error: any) => {
    const text = `${error?.shortMessage || ""} ${error?.message || ""}`.toLowerCase();
    return text.includes("user denied") || text.includes("user rejected");
  }, []);

  // Function to copy address to clipboard
  const copyAddressToClipboard = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedAddress(null), 2000);
      helperToast.success("Address copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy address:", error);
      helperToast.error("Failed to copy address");
    }
  };

  // Fetch ETH price from CoinGecko
  // Note: ETH on Arbitrum has the same price as ETH on mainnet
  const fetchETHPrice = useCallback(async () => {
    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd'
      );
      const data = await response.json();
      const price = data?.ethereum?.usd || 0;
      setEthPriceUSD(price);
      return price;
    } catch (error) {
      console.error("Error fetching ETH price:", error);
      // Fallback price if API fails
      setEthPriceUSD(3000);
      return 3000;
    }
  }, []);

  // Fetch ETH balance and check gas reserve
  const fetchETHBalance = useCallback(async () => {
    if (!publicClient || !account || wrongNetwork) {
      setEthBalance("0");
      setShowLowGasWarning(false);
      return;
    }

    try {
      const balance = await publicClient.getBalance({ address: account });
      const formatted = formatUnits(balance, 18);
      setEthBalance(formatted);
      return formatted;
    } catch (error) {
      console.error("Error fetching ETH balance:", error);
      setEthBalance("0");
      setShowLowGasWarning(false);
      return "0";
    }
  }, [account, publicClient, wrongNetwork]);

  // Check if user has sufficient gas
  const checkGasReserve = useCallback(async () => {
    if (!account || wrongNetwork) {
      setHasInsufficientGas(false);
      setShowLowGasWarning(false);
      return true;
    }

    const ethBal = await fetchETHBalance();
    const price = await fetchETHPrice();

    if (!ethBal || !price) {
      setHasInsufficientGas(false);
      setShowLowGasWarning(false);
      return true;
    }

    const ethBalanceNum = parseFloat(ethBal);
    const ethValueUSD = ethBalanceNum * price;

    // First check: If ETH balance in USD is less than gas reserve, show warning immediately
    if (ethValueUSD < GAS_RESERVE_USD) {
      setShowLowGasWarning(true);
      setHasInsufficientGas(true);
      return false;
    }

    // Second check: If depositing native ETH, check if balance minus deposit amount is >= gas reserve
    if (activeTab === 'deposit' && selectedToken?.isNative && amount) {
      const depositAmount = parseFloat(amount || "0");
      const remainingETH = ethBalanceNum - depositAmount;
      const remainingUSD = remainingETH * price;

      if (remainingUSD < GAS_RESERVE_USD) {
        setHasInsufficientGas(true);
        setShowLowGasWarning(false); // Don't show top warning, just disable button
        return false;
      }
    }

    setHasInsufficientGas(false);
    setShowLowGasWarning(false);
    return true;
  }, [activeTab, account, wrongNetwork, selectedToken, amount, fetchETHBalance, fetchETHPrice]);

  useEffect(() => {
    console.log("Connected chain:", chainId);
    setWrongNetwork(chainId !== ARBITRUM_CHAIN_ID);
  }, [chainId]);

  // Check gas reserve when modal opens
  useEffect(() => {
    if (isVisible && account && !wrongNetwork) {
      checkGasReserve();
    }
  }, [isVisible, account, wrongNetwork, checkGasReserve]);

  // Also check when amount or token changes
  useEffect(() => {
    if (isVisible && account && amount && selectedToken && !wrongNetwork) {
      checkGasReserve();
    }
  }, [amount, selectedToken]);

  const fetchBalance = useCallback(async () => {
    if (!publicClient || wrongNetwork || !selectedToken) {
      setMaxBalance("0.00");
      return;
    }

    // Determine which address to check based on active tab
    const addressToCheck = activeTab === 'deposit'
      ? account
      : custodyWalletAddress as `0x${string}`;

    if (!addressToCheck) {
      setMaxBalance("0.00");
      return;
    }

    try {
      setIsLoadingBalance(true);

      let balance;

      if (selectedToken.isNative) {
        balance = await publicClient.getBalance({ address: addressToCheck });
      } else {
        balance = await publicClient.readContract({
          address: selectedToken.address as `0x${string}`,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [addressToCheck]
        });
      }

      const formatted = formatUnits(balance, selectedToken.decimals);
      setMaxBalance(parseFloat(formatted).toFixed(6));
    } catch (error) {
      console.error("Error fetching balance:", error);
      setMaxBalance("0.00");
    } finally {
      setIsLoadingBalance(false);
    }
  }, [account, publicClient, wrongNetwork, selectedToken, activeTab, custodyWalletAddress]);

  useEffect(() => {
    if (isVisible && account && !wrongNetwork && selectedToken) {
      fetchBalance();
    }
  }, [isVisible, account, wrongNetwork, selectedToken, fetchBalance]);

  useEffect(() => {
    setAmount("");
    if (selectedToken && !wrongNetwork) {
      fetchBalance();
    }
  }, [selectedToken, activeTab]);

  const handleMaxClick = async () => {
    if (wrongNetwork) return;
    setAmount(maxBalance);
  };

  const handleSwitchNetwork = async () => {
    try {
      await switchChain({ chainId: ARBITRUM_CHAIN_ID });
      helperToast.success("Switched to Arbitrum Network");
    } catch (e) {
      console.error(e);
      helperToast.error("Failed to switch network");
    }
  };

  const handleTokenChange = (token: any) => {
    setSelectedToken(token);
  };

  // Fetch HL USDC balance when withdraw source switches to "hyperliquid"
  useEffect(() => {
    if (activeTab !== "withdraw" || withdrawSource !== "hyperliquid" || !custodyWalletAddress) {
      setHlUsdcBalance(null);
      return;
    }
    setIsLoadingHlBalance(true);
    getHlBalances(custodyWalletAddress)
      .then((bals: any[]) => {
        const usdc = bals.find((b) => b.asset === "USDC" || b.asset === "usdc");
        setHlUsdcBalance(parseFloat(usdc?.free ?? "0").toFixed(2));
      })
      .catch(() => setHlUsdcBalance("0"))
      .finally(() => setIsLoadingHlBalance(false));
  }, [activeTab, withdrawSource, custodyWalletAddress]);

  const handleHlWithdraw = useCallback(async () => {
    if (!authToken) {
      helperToast.error("Please log in first");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      helperToast.error("Invalid amount");
      return;
    }
    if (parseFloat(amount) <= 2) {
      helperToast.error("Minimum withdrawal from Hyperliquid is 1 USDC");
      return;
    }
    if (hlUsdcBalance !== null && parseFloat(amount) > parseFloat(hlUsdcBalance)) {
      helperToast.error("Insufficient HL balance");
      return;
    }
    try {
      setIsLoading(true);
      // Withdraw from HL perp account → custody wallet (destination defaults to custody)
      await withdrawFromHl(amount, authToken, custodyWalletAddress);
      helperToast.success(`Withdrawal of ${amount} USDC initiated. Funds arrive in your custody wallet in ~15 minutes.`);
      setAmount("");
      setIsLoadingHlBalance(true);
      getHlBalances(custodyWalletAddress)
        .then((bals: any[]) => {
          const usdc = bals.find((b) => b.asset === "USDC" || b.asset === "usdc");
          setHlUsdcBalance(parseFloat(usdc?.free ?? "0").toFixed(2));
        })
        .catch(() => {})
        .finally(() => setIsLoadingHlBalance(false));
    } catch (e: any) {
      helperToast.error(e.message ?? "HL withdrawal failed");
    } finally {
      setIsLoading(false);
    }
  }, [authToken, amount, hlUsdcBalance, custodyWalletAddress]);

  const handleDeposit = useCallback(async () => {
    if (!account || !walletClient || !publicClient) {
      helperToast.error("Connect your wallet");
      return;
    }

    if (wrongNetwork) {
      helperToast.error("Switch to Arbitrum first");
      return;
    }

    if (!selectedToken) {
      helperToast.error("Please select a token");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      helperToast.error("Invalid amount");
      return;
    }

    if (parseFloat(amount) > parseFloat(maxBalance)) {
      helperToast.error("Insufficient balance");
      return;
    }

    try {
      setIsLoading(true);

      const amountInWei = parseUnits(amount, selectedToken.decimals);
      let hash;

      if (selectedToken.isNative) {
        const estimate = await publicClient.estimateGas({
          account: account as `0x${string}`,
          to: custodyWalletAddress as `0x${string}`,
          value: amountInWei,
        });

        hash = await walletClient.sendTransaction({
          to: custodyWalletAddress as `0x${string}`,
          value: amountInWei,
          chain: arbitrum,
          gas: estimate,
        });
      } else {
        hash = await walletClient.writeContract({
          address: selectedToken.address as `0x${string}`,
          abi: ERC20_ABI,
          functionName: "transfer",
          args: [custodyWalletAddress as `0x${string}`, amountInWei],
          chain: arbitrum
        });
      }

      helperToast.success("Transaction submitted...");

      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === "success") {
        helperToast.success(`Deposited ${amount} ${selectedToken.symbol}`);

        setAmount("");
        await fetchBalance();
        await fetchETHBalance();

        setTimeout(() => setIsVisible(false), 1500);
      } else {
        helperToast.error("Transaction failed");
      }
    } catch (e: any) {
      console.error("Deposit error:", e);
      if (isUserRejectedError(e)) {
        helperToast.error("Transaction cancelled in wallet");
      } else if (`${e?.shortMessage || ""} ${e?.message || ""}`.toLowerCase().includes("intrinsic gas too low")) {
        helperToast.error("Gas limit too low for this transaction. Please retry.");
      } else {
        helperToast.error(e?.shortMessage || e?.message || "Deposit failed");
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    account,
    walletClient,
    publicClient,
    amount,
    maxBalance,
    custodyWalletAddress,
    fetchBalance,
    fetchETHBalance,
    wrongNetwork,
    selectedToken,
    checkGasReserve,
    isUserRejectedError
  ]);

  const handleWithdraw = useCallback(async () => {
    if (!account) {
      helperToast.error("Connect your wallet");
      return;
    }

    if (wrongNetwork) {
      helperToast.error("Switch to Arbitrum first");
      return;
    }

    if (!selectedToken) {
      helperToast.error("Please select a token");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      helperToast.error("Invalid amount");
      return;
    }

    if (parseFloat(amount) > parseFloat(maxBalance)) {
      helperToast.error("Insufficient balance in custody wallet");
      return;
    }

    try {
      setIsLoading(true);

      const amountInWei = parseUnits(amount, selectedToken.decimals).toString();

      let result;
      if (selectedToken.isNative) {
        result = await withdrawETH(account, amountInWei);
      } else {
        result = await withdrawToken(account, selectedToken.address, amountInWei);
      }

      helperToast.success(`Withdrawn ${amount} ${selectedToken.symbol}`);

      setAmount("");
      await fetchBalance();

      setTimeout(() => setIsVisible(false), 1500);
    } catch (e: any) {
      console.error("Withdraw error:", e);
      helperToast.error(e?.message || "Withdrawal failed");
    } finally {
      setIsLoading(false);
    }
  }, [
    account,
    amount,
    maxBalance,
    fetchBalance,
    wrongNetwork,
    selectedToken,
    setIsVisible
  ]);

  return (
    <Modal
      className="custody-wallet-modal"
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      label={activeTab === 'deposit' ? "Deposit to Custody Wallet" : "Withdraw from Custody Wallet"}
    >
      <div className="custody-wallet-content">
        {/* Tab buttons */}
        <div className="custody-wallet-tabs">
          <button
            className={`custody-wallet-tab ${activeTab === 'deposit' ? 'active' : ''}`}
            onClick={() => { setActiveTab('deposit'); setWithdrawSource('custody'); setAmount(''); }}
            disabled={isLoading}
          >
            Deposit
          </button>
          <button
            className={`custody-wallet-tab ${activeTab === 'withdraw' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdraw')}
            disabled={isLoading}
          >
            Withdraw
          </button>
        </div>

        {/* Deposit instruction message */}
        {activeTab === 'deposit' && (
          <div className="deposit-instruction">
            <div className="deposit-instruction-icon">💰</div>
            <div className="deposit-instruction-text">
              <div className="deposit-instruction-title">Deposit to Your Custody Wallet</div>
              <div className="deposit-instruction-subtitle">
                Add funds to your secure custody wallet to start trading on the platform
              </div>
            </div>
          </div>
        )}

        {wrongNetwork && (
          <div className="network-warning">
            <span className="network-warning-text">
              Wrong Network. Please switch to Arbitrum.
            </span>
            <button className="network-switch-button" onClick={handleSwitchNetwork}>
              Switch to Arbitrum
            </button>
          </div>
        )}


        {/* Source selector — only shown on Withdraw tab */}
        {activeTab === 'withdraw' && (
          <div className="deposit-field">
            <label className="deposit-label">Source</label>
            <div className="deposit-amount-container" style={{ display: "flex", gap: 0, padding: 0, overflow: "hidden" }}>
              {(["custody", "hyperliquid"] as WithdrawSource[]).map((src) => (
                <button
                  key={src}
                  onClick={() => { setWithdrawSource(src); setAmount(""); }}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: "8px 0",
                    background: withdrawSource === src ? "#3b82f6" : "transparent",
                    color: withdrawSource === src ? "#fff" : "#94a3b8",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {src === "custody" ? "GMX" : "Hyperliquid"}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Asset — locked to USDC when HL source */}
        <div className="deposit-field">
          <label className="deposit-label">Asset</label>
          {activeTab === 'withdraw' && withdrawSource === 'hyperliquid' ? (
            <div className="deposit-amount-container" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>USDC</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>only asset available from Hyperliquid</span>
            </div>
          ) : selectedToken ? (
            <div className="deposit-amount-container deposit-amount-container-clickable">
              <TokenSelector
                chainId={ARBITRUM_CHAIN_ID}
                tokenAddress={selectedToken.address}
                tokens={arbitrumTokens}
                infoTokens={tokensData}
                onSelectToken={handleTokenChange}
                showSymbolImage={true}
                showTokenImgInDropdown={true}
                showBalances={true}
              />
            </div>
          ) : null}
        </div>

        <div className="deposit-field">
          <label className="deposit-label">
            {activeTab === 'deposit' ? 'Deposit Chain' : 'Withdraw Chain'}
          </label>
          <input disabled className="deposit-amount-container" defaultValue="Arbitrum" />
        </div>

        <div className="deposit-field">
          <label className="deposit-label">Amount</label>
          <div className="deposit-amount-container">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="deposit-amount-input"
              disabled={isLoading || (activeTab === 'withdraw' && withdrawSource === 'custody' && wrongNetwork)}
              step="0.01"
            />
            <span
              className="deposit-max"
              onClick={() => {
                if (activeTab === 'withdraw' && withdrawSource === 'hyperliquid') {
                  setAmount(hlUsdcBalance ?? "0");
                } else if (!wrongNetwork) {
                  handleMaxClick();
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {activeTab === 'withdraw' && withdrawSource === 'hyperliquid'
                ? isLoadingHlBalance ? "Loading..." : `MAX: ${hlUsdcBalance ?? "0"} USDC`
                : isLoadingBalance ? "Loading..." : `MAX: ${parseFloat(maxBalance || '0').toFixed(6)} ${selectedToken?.symbol || ''}`
              }
            </span>
          </div>
        </div>

        {activeTab === 'withdraw' && withdrawSource === 'hyperliquid' && (
          <p style={{ fontSize: "12px", color: "#888", margin: "4px 0 8px" }}>
            Minimum withdrawal: 1 USDC. Funds arrive in ~15 minutes.
          </p>
        )}

        <div className="deposit-info">
          <span className="deposit-info-label">
            {activeTab === 'deposit' ? 'Depositing to:' : 'Withdrawing to:'}
          </span>
          <span
            className={`deposit-info-address ${copiedAddress ? 'copied' : ''}`}
            onClick={() => {
              const addressToCopy = activeTab === 'deposit'
                ? custodyWalletAddress
                : withdrawSource === 'hyperliquid'
                  ? custodyWalletAddress
                  : account as string;
              if (addressToCopy) copyAddressToClipboard(addressToCopy);
            }}
            style={{ cursor: 'pointer' }}
          >
            {activeTab === 'deposit'
              ? `${custodyWalletAddress.slice(0, 6)}...${custodyWalletAddress.slice(-4)}`
              : withdrawSource === 'hyperliquid'
                ? `Custody ${custodyWalletAddress.slice(0, 6)}...${custodyWalletAddress.slice(-4)}`
                : account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'N/A'
            }
            {copiedAddress && <span className="copy-indicator"> ✓</span>}
          </span>
        </div>

        {activeTab === 'withdraw' && withdrawSource === 'hyperliquid' && (
          <div className="hl-fee-notice">
            <span className="hl-fee-notice-icon">⏱</span>
            <div className="hl-fee-notice-body">
              <div className="hl-fee-notice-row">
                <span>Network fee</span>
                <span>~$1 USDC</span>
              </div>
              <div className="hl-fee-notice-row">
                <span>You receive</span>
                <span>{amount && parseFloat(amount) > 1 ? (parseFloat(amount) - 1).toFixed(2) : "—"} USDC</span>
              </div>
              <div className="hl-fee-notice-sub">Arrives in ~15 min · then withdraw via the GMX tab</div>
            </div>
          </div>
        )}

        <button
          className="deposit-button"
          onClick={
            activeTab === 'deposit' ? handleDeposit
            : withdrawSource === 'hyperliquid' ? handleHlWithdraw
            : handleWithdraw
          }
          disabled={
            isLoading || !amount ||
            (activeTab === 'withdraw' && withdrawSource === 'custody' && (wrongNetwork || !selectedToken))
          }
        >
          {isLoading ? (
            <>
              <span className="spinner"></span> Processing...
            </>
          ) : (
            activeTab === 'deposit' ? 'Deposit' : 'Withdraw'
          )}
        </button>

        {!account && (
          <div className="deposit-warning">Please connect your wallet</div>
        )}
      </div>
    </Modal>
  );
}